import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Number stepper (−/value/+) from the component kit (PROMANG_COMPONENTS.md §2 «Stepper»).
 * Controlled: `value` + `onChange(nextNumber)`. Typing is allowed; the value is clamped
 * to [min, max] on blur and on the buttons.
 */
export const PMStepper = ({
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  suffix,
  disabled = false,
  id,
  ariaLabel,
  className = "",
}) => {
  const { t } = useTranslation();
  const numeric = Number.isFinite(Number(value)) ? Number(value) : min;
  const clamp = (next) => Math.min(max, Math.max(min, next));
  const [draft, setDraft] = React.useState(String(numeric));

  React.useEffect(() => {
    setDraft(String(numeric));
  }, [numeric]);

  const commit = (raw) => {
    const parsed = parseInt(raw, 10);
    const next = clamp(Number.isNaN(parsed) ? numeric : parsed);
    setDraft(String(next));
    if (next !== numeric) onChange?.(next);
  };

  return (
    <span className={`pm-stepper ${className}`.trim()}>
      <button
        type="button"
        className="pm-stepper__btn"
        aria-label={t("pmStepper.decrease")}
        disabled={disabled || numeric <= min}
        onClick={() => commit(numeric - step)}
      >
        −
      </button>
      <input
        id={id}
        className="pm-stepper__value"
        inputMode="numeric"
        aria-label={ariaLabel}
        disabled={disabled}
        value={draft}
        onChange={(event) => setDraft(event.target.value.replace(/[^\d]/g, ""))}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowUp") {
            event.preventDefault();
            commit(numeric + step);
          } else if (event.key === "ArrowDown") {
            event.preventDefault();
            commit(numeric - step);
          } else if (event.key === "Enter") {
            event.preventDefault();
            commit(event.currentTarget.value);
          }
        }}
      />
      <button
        type="button"
        className="pm-stepper__btn"
        aria-label={t("pmStepper.increase")}
        disabled={disabled || numeric >= max}
        onClick={() => commit(numeric + step)}
      >
        +
      </button>
      {suffix ? <span className="pm-stepper__suffix">{suffix}</span> : null}
    </span>
  );
};

export default PMStepper;
